import { useMemo, useState } from "react";
import { useClerk } from "@clerk/react-router";
import { LogOut01 } from "@untitledui/icons";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import { NavItemButton } from "@/components/application/app-navigation/base-components/nav-item-button";
import { BridgeHealthIndicator } from "@/components/application/bridge-health-indicator";
import { useAcomAdmin } from "@/hooks/use-acom-admin";
import { useClientBridge } from "@/hooks/use-client-bridge";
import { cx } from "@/utils/cx";

export default function AppLayout() {
    const location = useLocation();
    const navigate = useNavigate();
    const { signOut } = useClerk();
    const [isLoggingOut, setIsLoggingOut] = useState(false);
    const { isAcomAdmin } = useAcomAdmin();
    const { hasBridge } = useClientBridge();

    // Navigation items: none for admin (admin has own nav); legacy tabs for non-admin
    // Must be called before early return to satisfy rules of hooks
    const navItems = useMemo(() => {
        if (isAcomAdmin) return [];
        return [
            { id: "home", label: "Home", href: "/home" },
            { id: "legacy-home", label: "Legacy Home", href: "/legacy-home" },
            { id: "legacy-files", label: "Legacy Files", href: "/legacy-files" },
            { id: "legacy-documents", label: "Legacy Documents", href: "/legacy-documents" },
        ];
    }, [isAcomAdmin]);

    if (isAcomAdmin && !location.pathname.startsWith("/admin")) {
        return <Navigate to="/admin" replace />;
    }

    const isCurrentPath = (href: string) => {
        return location.pathname === href || location.pathname.startsWith(href + "/");
    };

    const handleNavigation = (href: string) => {
        navigate(href);
    };

    const handleLogout = async (e: React.MouseEvent) => {
        e.preventDefault();
        if (isLoggingOut) return; // Prevent double-click

        setIsLoggingOut(true);
        try {
            await signOut();
            // Clerk will handle the redirect, but we can also navigate explicitly
            navigate("/login", { replace: true });
        } catch (error) {
            console.error("Logout failed:", error);
            setIsLoggingOut(false);
        }
    };

    return (
        <div className="flex h-screen flex-col bg-primary">
            {/* Navigation Bar */}
            <div className="sticky top-0 z-50 flex-shrink-0 border-b border-secondary bg-primary px-6">
                <div className="relative flex h-16 items-center justify-between">
                    {/* Centered Navigation Items */}
                    <div className="absolute left-1/2 flex h-full -translate-x-1/2 items-center gap-8">
                        {navItems.map((item) => (
                            <button
                                key={item.id}
                                onClick={() => handleNavigation(item.href)}
                                className={cx(
                                    "flex h-full cursor-pointer items-center gap-2 rounded-none border-b-2 px-1 text-md font-semibold transition duration-100 ease-linear",
                                    isCurrentPath(item.href)
                                        ? "border-fg-brand-primary_alt text-brand-secondary"
                                        : "border-transparent text-quaternary hover:border-fg-brand-primary_alt hover:text-brand-secondary",
                                )}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>

                    {/* Right Side Actions */}
                    <div className="ml-auto flex items-center gap-2">
                        {hasBridge && <BridgeHealthIndicator />}
                        <NavItemButton size="md" icon={LogOut01} label="Sign out" onClick={handleLogout} tooltipPlacement="bottom" />
                    </div>
                </div>
            </div>

            {/* Main Content Area */}
            <main className="min-h-0 flex-1 overflow-hidden">
                <Outlet />
            </main>
        </div>
    );
}
